import React, { useState, useEffect } from 'react';
import chemicalService from '../../../services/chemicalService';

const PAYMENT_MODES = ['cash', 'cheque'];

const CommissionFormModal = ({ onClose, onSaved }) => {
  const [products, setProducts] = useState([]);
  const [saleLines, setSaleLines] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingLines, setLoadingLines] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [productId, setProductId] = useState('');
  const [form, setForm] = useState({
    sale_item_id: '',
    agent_name: '',
    percent: '',
    amount: '',
    paid_by: '',
    payment_mode: 'cash',
    bank_name: '',
    cheque_number: '',
    date: new Date().toISOString().split('T')[0],
    notes: '',
  });

  useEffect(() => {
    const load = async () => {
      try {
        setLoadingProducts(true);
        const res = await chemicalService.getProducts({ page: 1, limit: 1000 });
        setProducts(res.data || []);
      } catch (err) {
        console.error('Error loading products:', err);
      } finally {
        setLoadingProducts(false);
      }
    };
    load();
  }, []);

  // Load uncommissioned sale lines when product changes
  const handleProductChange = async (pid) => {
    setProductId(pid);
    setForm((prev) => ({ ...prev, sale_item_id: '', percent: '', amount: '' }));
    setSaleLines([]);
    if (!pid) return;
    try {
      setLoadingLines(true);
      const res = await chemicalService.getUncommissionedSaleLines(pid);
      setSaleLines(res.data || []);
    } catch (err) {
      console.error('Error loading sale lines:', err);
    } finally {
      setLoadingLines(false);
    }
  };

  const selectedLine = saleLines.find((l) => String(l.sale_item_id) === String(form.sale_item_id));
  const baseAmount = selectedLine ? parseFloat(selectedLine.amount) || 0 : 0;

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  // When percent changes, auto-calc amount off base line amount
  const handlePercentChange = (val) => {
    const pct = parseFloat(val) || 0;
    setForm((prev) => ({
      ...prev,
      percent: val,
      amount: baseAmount > 0 && pct > 0 ? ((baseAmount * pct) / 100).toFixed(2) : prev.amount,
    }));
  };

  const fmtMoney = (n) => 'Rs ' + (Math.round((n || 0) * 100) / 100).toLocaleString('en-PK');
  const fmtQty = (n) => parseFloat(n || 0).toFixed(2).replace(/\.?0+$/, '');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!productId) return alert('❌ Select a product.');
    if (!form.sale_item_id) return alert('❌ Select the sale this commission is for.');
    if (!form.agent_name.trim()) return alert('❌ Agent name is required.');
    if (!form.amount || parseFloat(form.amount) <= 0) return alert('❌ Commission amount must be > 0.');
    if (parseFloat(form.amount) > baseAmount) {
      if (!window.confirm(`Commission (${fmtMoney(form.amount)}) is more than the sale line amount (${fmtMoney(baseAmount)}). Continue?`)) return;
    }
    if (form.payment_mode === 'cheque') {
      if (!form.bank_name.trim()) return alert('❌ Bank Name is required for cheque.');
      if (!form.cheque_number.trim()) return alert('❌ Cheque Number is required for cheque.');
    }

    try {
      setSubmitting(true);
      const payload = {
        sale_item_id: parseInt(form.sale_item_id, 10),
        agent_name: form.agent_name.trim(),
        percent: form.percent ? parseFloat(form.percent) : null,
        amount: parseFloat(form.amount),
        paid_by: form.paid_by.trim() || null,
        payment_mode: form.payment_mode,
        bank_name: form.payment_mode === 'cheque' ? (form.bank_name.trim() || null) : null,
        cheque_number: form.payment_mode === 'cheque' ? (form.cheque_number.trim() || null) : null,
        date: form.date,
        notes: form.notes.trim() || null,
      };
      await chemicalService.createCommission(payload);
      alert('✅ Commission recorded! It now shows in the product ledger and sale ledger.');
      onSaved();
    } catch (err) {
      const msg = err.response?.data?.message || err.message || 'Failed to save commission';
      alert('❌ ' + msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>

        <div style={styles.modalHeader}>
          <div>
            <h2 style={styles.modalTitle}>🤝 New Commission</h2>
            <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '4px 0 0 0' }}>
              Record commission against a specific sale — one per sale line
            </p>
          </div>
          <button onClick={onClose} style={styles.closeButton}>✕</button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: '24px 30px' }}>

            <div style={styles.sectionLabel}>Link to Sale</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Product <span style={styles.required}>*</span></label>
                <select
                  value={productId}
                  onChange={(e) => handleProductChange(e.target.value)}
                  style={styles.input}
                  required
                  disabled={loadingProducts}
                >
                  <option value="">{loadingProducts ? 'Loading...' : '-- Select product --'}</option>
                  {products.map((p) => (
                    <option key={p.id} value={p.id}>{p.code} — {p.name}</option>
                  ))}
                </select>
              </div>
              <div style={{ ...styles.formGroup, flex: 2 }}>
                <label style={styles.label}>Sale / Invoice <span style={styles.required}>*</span></label>
                <select
                  value={form.sale_item_id}
                  onChange={(e) => handleChange('sale_item_id', e.target.value)}
                  style={styles.input}
                  required
                  disabled={!productId || loadingLines}
                >
                  <option value="">
                    {!productId ? '-- Pick product first --'
                      : loadingLines ? 'Loading sales...'
                      : saleLines.length === 0 ? '-- No un-commissioned sales for this product --'
                      : '-- Select sale --'}
                  </option>
                  {saleLines.map((l) => (
                    <option key={l.sale_item_id} value={l.sale_item_id}>
                      {l.invoice_no} · {l.customer_name || 'Walk-in'} · {fmtQty(l.quantity)}{l.unit} @ {fmtMoney(l.rate)} = {fmtMoney(l.amount)}
                    </option>
                  ))}
                </select>
                <span style={styles.hint}>Only sales without an existing commission are shown.</span>
              </div>
            </div>

            {selectedLine && (
              <div style={styles.baseBox}>
                Sale line amount (commission base): <strong>{fmtMoney(baseAmount)}</strong>
                <span style={{ color: '#7f8c8d', marginLeft: 8 }}>
                  ({fmtQty(selectedLine.quantity)}{selectedLine.unit} × {fmtMoney(selectedLine.rate)})
                </span>
              </div>
            )}

            <div style={styles.sectionLabel}>Commission</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Agent Name <span style={styles.required}>*</span></label>
                <input
                  type="text"
                  value={form.agent_name}
                  onChange={(e) => handleChange('agent_name', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. Imran (purchase officer)"
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Percent %</label>
                <input
                  type="number"
                  step="0.001"
                  min="0"
                  value={form.percent}
                  onChange={(e) => handlePercentChange(e.target.value)}
                  style={styles.input}
                  placeholder="optional — auto-calcs amount"
                  disabled={!selectedLine}
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Amount (Rs) <span style={styles.required}>*</span></label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.amount}
                  onChange={(e) => handleChange('amount', e.target.value)}
                  style={styles.input}
                  placeholder="0"
                  required
                />
              </div>
            </div>

            <div style={styles.sectionLabel}>Payment</div>
            <div style={styles.row}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Date <span style={styles.required}>*</span></label>
                <input
                  type="date"
                  value={form.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  onClick={(e) => e.target.showPicker?.()}
                  style={styles.input}
                  required
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Paid By</label>
                <input
                  type="text"
                  value={form.paid_by}
                  onChange={(e) => handleChange('paid_by', e.target.value)}
                  style={styles.input}
                  placeholder="e.g. Owner, Cash Account"
                />
              </div>
              <div style={styles.formGroup}>
                <label style={styles.label}>Payment Mode <span style={styles.required}>*</span></label>
                <select
                  value={form.payment_mode}
                  onChange={(e) => handleChange('payment_mode', e.target.value)}
                  style={styles.input}
                  required
                >
                  {PAYMENT_MODES.map((m) => (
                    <option key={m} value={m}>{m === 'cash' ? 'Cash' : 'Cheque'}</option>
                  ))}
                </select>
              </div>
            </div>

            {form.payment_mode === 'cheque' && (
              <div style={styles.row}>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Bank Name <span style={styles.required}>*</span></label>
                  <input type="text" value={form.bank_name} onChange={(e) => handleChange('bank_name', e.target.value)} style={styles.input} placeholder="e.g. HBL" required />
                </div>
                <div style={styles.formGroup}>
                  <label style={styles.label}>Cheque Number <span style={styles.required}>*</span></label>
                  <input type="text" value={form.cheque_number} onChange={(e) => handleChange('cheque_number', e.target.value)} style={styles.input} placeholder="e.g. 123456" required />
                </div>
              </div>
            )}

            <div style={styles.formGroup}>
              <label style={styles.label}>Notes (optional)</label>
              <textarea
                value={form.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                style={{ ...styles.input, minHeight: '50px', resize: 'vertical', fontFamily: 'inherit' }}
                rows="2"
                placeholder="Any extra detail..."
              />
            </div>

          </div>

          <div style={styles.modalFooter}>
            <button type="button" onClick={onClose} style={styles.cancelButton} disabled={submitting}>Cancel</button>
            <button type="submit" style={styles.submitButton} disabled={submitting}>
              {submitting ? '⏳ Saving...' : '💾 Record Commission'}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};

const styles = {
  modalOverlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' },
  modal: { background: 'white', borderRadius: '12px', width: '95%', maxWidth: '820px', maxHeight: '92vh', overflow: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalHeader: { padding: '20px 30px', borderBottom: '2px solid #f0f0f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', position: 'sticky', top: 0, background: 'white', zIndex: 10 },
  modalTitle: { fontSize: '22px', fontWeight: '700', color: '#2c3e50', margin: 0 },
  closeButton: { padding: '8px 16px', background: '#f0f0f0', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '20px', fontWeight: '700', color: '#666' },
  sectionLabel: { fontSize: '12px', fontWeight: '700', color: '#7f8c8d', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: '18px', marginBottom: '12px', paddingBottom: '6px', borderBottom: '1px solid #f0f0f0' },
  row: { display: 'flex', gap: '16px', flexWrap: 'wrap' },
  formGroup: { flex: 1, minWidth: '180px', display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '12px' },
  label: { fontSize: '13px', fontWeight: '600', color: '#2c3e50' },
  required: { color: '#e74c3c' },
  input: { width: '100%', padding: '10px 12px', border: '2px solid #e0e0e0', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box', fontFamily: 'inherit' },
  hint: { fontSize: '11px', color: '#95a5a6', fontStyle: 'italic' },
  baseBox: { background: '#e8f4fd', borderRadius: '8px', padding: '10px 14px', fontSize: '14px', color: '#1565c0', marginBottom: '4px' },
  modalFooter: { padding: '16px 30px', borderTop: '2px solid #f0f0f0', display: 'flex', justifyContent: 'flex-end', gap: '12px', position: 'sticky', bottom: 0, background: 'white' },
  cancelButton: { padding: '12px 24px', background: '#95a5a6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  submitButton: { padding: '12px 28px', background: '#e74c3c', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
};

export default CommissionFormModal;